import { Component, ViewEncapsulation, HostListener } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Menu, NavService } from '../../services/nav.service';
import { LayoutService } from '../../services/layout.service';
import { ModuleService } from '../../../services/module.service';
import { AuthService } from '../../../services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalizationDataService } from '../../../services/data/localization-data.service';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { SubscriptionService } from '../../../services/subscription.service';
import { TourService } from '../../../services/guided-tour.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SidebarComponent {

  public iconSidebar: any;
  public menuItems: Menu[] = [];
  public groupedMenus: { group: string; items: Menu[] }[] = []; // <— grouped view
  private selectedItem: Menu | null = null;

  public allMenuItems: Menu[] = []; // Cache for all menu items
  public url: any;
  public fileurl: any;

  // For Horizontal Menu
  public margin: any = 0;
  public width: any = window.innerWidth;
  public leftArrowNone: boolean = true;
  public rightArrowNone: boolean = false;
  private subscriptionStatusSubscription: Subscription; // Store subscription observer

  // Minimal defaults, already grouped
  public defaultMenus: Menu[] = [
    {
      title: "Groups",
      icon: "fas fa-users",
      type: "link",
      badgeType: "light-primary",
      path: "/groups",
      hasPermission: this.isSuperAdmin(),
      group: "admin"
    } as any,
    {
      title: "Modules",
      icon: "fas fa-puzzle-piece",
      type: "link",
      badgeType: "light-primary",
      path: "/modules",
      hasPermission: this.isSuperAdmin(),
      group: "admin"
    } as any,
    {
      title: "Training Videos",
      icon: "fas fa-video",
      type: "link",
      badgeType: "light-primary",
      path: "/training-videos",
      hasPermission: this.isSuperAdmin(),
      group: "training"
    } as any,
    {
      title: "Subscriptions",
      icon: "fas fa-credit-card",
      type: "link",
      badgeType: "light-primary",
      path: "/subscriptions/list",
      hasPermission: this.isSuperAdmin(),
      group: "admin"
    } as any
  ];  

  constructor(
    private router: Router, 
    public moduleService: ModuleService,
    public navServices: NavService,
    public layout: LayoutService,
    private authService: AuthService,
    private translate: TranslateService,
    private localizationDataService: LocalizationDataService,
    private subscriptionService: SubscriptionService,
    private tourService: TourService
  ) {}

  ngOnInit(): void {
    this.localizationDataService.currentLanguage$.subscribe((currentLang: any) => {
      if (currentLang) {
        this.updateMenuTranslations(currentLang.code);
      }
    });

    this.subscriptionStatusSubscription = this.subscriptionService.subscriptionStatus$
      .pipe(distinctUntilChanged())
      .subscribe((isActive) => {
        if (isActive) {
          this.loadMenuItems(); // Reload menus when active
        } else {
          this.allMenuItems = this.defaultMenus; // Show default menus when inactive
          this.updateMenuTranslations(this.translate.currentLang || this.translate.defaultLang);
        }
      });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.menuItems.filter(items => {
          if (items.path === event.url) {
            this.setNavActive(items);
          }
          if (items.children) {
            items.children.filter(subItems => {
              if (subItems.path === event.url) {
                this.setNavActive(subItems);
              }
              if (subItems.children) {
                subItems.children.filter(subSubItems => {
                  if (subSubItems.path === event.url) {
                    this.setNavActive(subSubItems);
                  }
                });
              }
            });
          }
        });
      }
    });
  }

  loadMenuItems(): void {
    this.moduleService.getMenus().subscribe(
      menuItems => {
        this.allMenuItems = menuItems;
        this.updateMenuTranslations(this.translate.currentLang || this.translate.defaultLang);
        setTimeout(() => this.tourService.startMenuTour(this.menuItems), 1000);
      },
      error => {
        console.error('Failed to load menus:', error);
        this.allMenuItems = this.defaultMenus;
        this.updateMenuTranslations(this.translate.currentLang || this.translate.defaultLang);
      }
    );
  }

  /** Translate titles, ensure group exists, then rebuild grouped view */
  updateMenuTranslations(currentLang: string): void {
    const translateMenuItems = (items: any[]): Menu[] => {
      return (items || []).map((item) => ({
        ...item,
        title: item.translations?.[currentLang] || item.title,
        group: (item.group || 'extra').toString().toLowerCase(),
        children: item.children ? translateMenuItems(item.children) : []
      }));
    };
  
    this.menuItems = translateMenuItems(this.allMenuItems);
    this.buildGroups();
  }

  /** Small helper: bucket by `group`, sort groups and items for scanability */
  private buildGroups(): void {
    const order = [
      'dashboards','pms','lms','recruitment','hr','mygoperform',
      'communication','accounting','purchase','sales','documents',
      'system','training','training-adjustment','admin','extra'
    ];

    const buckets = new Map<string, Menu[]>();
    (this.menuItems || []).forEach(m => {
      if (!m?.hasPermission) return;
      const g = (m.group || 'extra').toLowerCase();
      if (!buckets.has(g)) buckets.set(g, []);
      buckets.get(g)!.push(m);
    });

    const groups = Array.from(buckets.entries())
      .map(([group, items]) => ({
        group,
        items: items.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
      }));

    this.groupedMenus = groups.sort((a, b) => {
      const ia = order.indexOf(a.group);
      const ib = order.indexOf(b.group);
      if (ia === -1 && ib === -1) return a.group.localeCompare(b.group);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }  

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.width = event.target.innerWidth - 500;
  }

  sidebarToggle() {
    this.navServices.collapseSidebar = !this.navServices.collapseSidebar;
  }

  // Active Nav state
  setNavActive(item: Menu) {
    this.menuItems.filter(menuItem => {
      if (menuItem !== item) {
        menuItem.active = false;
      }
      if (menuItem.children && menuItem.children.includes(item)) {
        menuItem.active = true;
      }
      if (menuItem.children) {
        menuItem.children.filter(submenuItems => {
          if (submenuItems.children && submenuItems.children.includes(item)) {
            menuItem.active = true;
            submenuItems.active = true;
          }
        });
      }
    });
  }

  // Click Toggle menu
  toggletNavActive(item: Menu) {
    if (!item.active) {
      this.menuItems.forEach(a => {
        if (this.menuItems.includes(item)) {
          a.active = false;
        }
        if (!a.children) { return; }
        a.children.forEach(b => {
          if (a.children.includes(item)) {
            b.active = false;
          }
        });
      });
    }
    item.active = !item.active;
  }

  // For Horizontal Menu
  scrollToLeft() {
    if (this.margin >= -this.width) {
      this.margin = 0;
      this.leftArrowNone = true;
      this.rightArrowNone = false;
    } else {
      this.margin += this.width;
      this.rightArrowNone = false;
    }
  }

  scrollToRight() {
    if (this.margin <= -3051) {
      this.margin = -3464;
      this.leftArrowNone = false;
      this.rightArrowNone = true;
    } else {
      this.margin += -this.width;
      this.leftArrowNone = false;
    }
  }

  isSuperAdmin(): boolean {
    if (this.authService.isLoggedIn()) {
      return this.authService.isSuperAdmin();
    }
    return false;
  }

  ngOnDestroy(): void {
    if (this.subscriptionStatusSubscription) {
      this.subscriptionStatusSubscription.unsubscribe();
    }
  }

  toggleMenuAndTour(item: Menu): void {
    if (!item.active) {
      this.toggletNavActive(item);
      this.startSubMenuTour(item);
      this.selectedItem = item;
    } else {
      this.toggletNavActive(item);
      this.selectedItem = null;
    }
  }

  startSubMenuTour(item: Menu): void {
    if (item.children && item.children.length > 0) {
      setTimeout(() => this.tourService.startMenuTour(item.children), 1000);
    }
  }  

  restartTour(): void {
    this.tourService.enableTourAgain();
    this.tourService.startMenuTour(this.selectedItem?.children || this.menuItems);
  }

  parseRouterLink(path: string): { link: any[]; queryParams?: any } {
    if (!path) return { link: [] };

    const [url, query] = path.split('?');

    if (!query) {
      return { link: [url] }; // simple route
    }

    // Convert query string into object
    const params: any = {};
    query.split('&').forEach(pair => {
      const [key, val] = pair.split('=');
      if (key) params[key] = decodeURIComponent(val ?? '');
    });

    return { link: [url], queryParams: params };
  }
}
